import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { EventClickArg, DatesSetArg } from "@fullcalendar/core";
import { useAuthContext } from "../contexts/AuthContext";
import ScheduleModal from "../components/ScheduleModal";
import styles from "./AdminInstagramCalendar.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import { isQBankCode, QBankCode } from "../utils/qbankProfile";

interface CalendarPost {
    id: string;
    scheduled_for: string;
    question_preview: string;
    specialty: string;
    status: string;
    scheduling_status: string;
    has_images: boolean;
}

interface CalendarData {
    [date: string]: CalendarPost[];
}

interface CalendarEvent {
    id: string;
    title: string;
    start: string;
    backgroundColor: string;
    borderColor: string;
    extendedProps: {
        post: CalendarPost;
    };
}

const STATUS_COLORS: Record<string, { bg: string; border: string }> = {
    scheduled: { bg: "#2563eb", border: "#1d4ed8" },
    completed: { bg: "#16a34a", border: "#15803d" },
    posted: { bg: "#16a34a", border: "#15803d" },
    failed: { bg: "#dc2626", border: "#b91c1c" },
    unscheduled: { bg: "#6b7280", border: "#4b5563" },
};

const AdminInstagramCalendar: React.FC = () => {
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const bankParam = searchParams.get("bank") || undefined;
    const bank: QBankCode = isQBankCode(bankParam) ? bankParam : "sdle";

    const [calendarData, setCalendarData] = useState<CalendarData>({});
    const [initialLoading, setInitialLoading] = useState(true);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);

    // Modal state
    const [selectedPost, setSelectedPost] = useState<CalendarPost | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [reschedulingPostId, setReschedulingPostId] = useState<string | null>(null);
    const [reschedulingInitialDate, setReschedulingInitialDate] = useState<string | null>(null);

    // Loading states
    const [actionLoading, setActionLoading] = useState(false);

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

    const loadCalendarData = useCallback(async () => {
        if (!user?.id) return;

        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/calendar?user_id=${user.id}&year=${currentYear}&month=${currentMonth}&bank=${bank}`
            );
            if (res.ok) {
                const data = await res.json();
                setCalendarData(data);
            }
        } catch (err) {
            console.error("Failed to load calendar data:", err);
        } finally {
            setInitialLoading(false);
        }
    }, [apiUrl, user?.id, currentYear, currentMonth, bank]);

    useEffect(() => {
        loadCalendarData();
    }, [loadCalendarData]);

    const handleDatesSet = (dateInfo: DatesSetArg) => {
        // Use view.currentStart to get the actual displayed month
        // (dateInfo.start can be from previous month due to trailing days)
        const viewStart = dateInfo.view.currentStart;
        const newYear = viewStart.getFullYear();
        const newMonth = viewStart.getMonth() + 1;

        // Only update if month/year actually changed to prevent unnecessary re-fetches
        if (newYear !== currentYear || newMonth !== currentMonth) {
            setCurrentYear(newYear);
            setCurrentMonth(newMonth);
        }
    };

    const handleEventClick = (clickInfo: EventClickArg) => {
        const post = clickInfo.event.extendedProps.post as CalendarPost;
        setSelectedPost(post);
        setShowDetailModal(true);
    };

    const handleReschedule = (postId: string, currentScheduledFor?: string) => {
        setReschedulingPostId(postId);
        // Build full ISO string from date + time
        if (currentScheduledFor && selectedPost) {
            // Find the date from calendarData
            const dateKey = Object.keys(calendarData).find(date =>
                calendarData[date].some(p => p.id === postId)
            );
            if (dateKey) {
                setReschedulingInitialDate(`${dateKey}T${currentScheduledFor}:00Z`);
            }
        } else {
            setReschedulingInitialDate(null);
        }
        setShowDetailModal(false);
        setShowScheduleModal(true);
    };

    const handleScheduleSubmit = async (datetime: Date) => {
        if (!user?.id || !reschedulingPostId) return;

        setActionLoading(true);
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/${reschedulingPostId}/schedule`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        user_id: user.id,
                        scheduled_for: datetime.toISOString(),
                    }),
                }
            );

            if (res.ok) {
                loadCalendarData();
                setShowScheduleModal(false);
                setReschedulingPostId(null);
                setReschedulingInitialDate(null);
            } else {
                const error = await res.json();
                alert(error.error || "Failed to reschedule");
            }
        } catch (err) {
            console.error("Failed to reschedule:", err);
        } finally {
            setActionLoading(false);
        }
    };

    const handleMarkPosted = async (postId: string) => {
        if (!user?.id) return;

        setActionLoading(true);
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/${postId}/mark-posted`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ user_id: user.id }),
                }
            );

            if (res.ok) {
                loadCalendarData();
                setShowDetailModal(false);
            }
        } catch (err) {
            console.error("Failed to mark as posted:", err);
        } finally {
            setActionLoading(false);
        }
    };

    const handleUnschedule = async (postId: string) => {
        if (!user?.id) return;

        // Confirmation dialog
        if (!window.confirm("Are you sure you want to unschedule this post?")) {
            return;
        }

        setActionLoading(true);
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/${postId}/schedule`,
                {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ user_id: user.id }),
                }
            );

            if (res.ok) {
                loadCalendarData();
                setShowDetailModal(false);
            }
        } catch (err) {
            console.error("Failed to unschedule:", err);
        } finally {
            setActionLoading(false);
        }
    };

    const handleRetry = async (postId: string) => {
        if (!user?.id) return;

        setActionLoading(true);
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/${postId}/retry`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ user_id: user.id }),
                }
            );

            const data = await res.json();

            if (data.success) {
                loadCalendarData();
                setShowDetailModal(false);
                alert("Successfully posted to Instagram!");
            } else {
                alert(`Failed to retry: ${data.error || "Unknown error"}`);
            }
        } catch (err) {
            console.error("Failed to retry:", err);
            alert("Failed to retry: Network error");
        } finally {
            setActionLoading(false);
        }
    };

    // Convert calendar data to FullCalendar events
    const events: CalendarEvent[] = Object.entries(calendarData).flatMap(
        ([date, posts]) =>
            posts.map((post) => {
                const colors = STATUS_COLORS[post.scheduling_status] || STATUS_COLORS.unscheduled;
                return {
                    id: post.id,
                    title: post.question_preview.slice(0, 30) + "...",
                    start: `${date}T${post.scheduled_for}:00`,
                    backgroundColor: colors.bg,
                    borderColor: colors.border,
                    extendedProps: { post },
                };
            })
    );

    const formatTime = (timeStr: string): string => {
        const [hours, minutes] = timeStr.split(":").map(Number);
        const period = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
    };

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <button
                    className={styles.backButton}
                    onClick={() => navigate(`/admin/instagram?bank=${bank}`)}
                >
                    Back to Content
                </button>
                <h1>{bank.toUpperCase()} Content Calendar</h1>
                <div className={styles.legend}>
                    <span className={styles.legendItem}>
                        <span className={styles.legendDot} style={{ background: "#2563eb" }} />
                        Scheduled
                    </span>
                    <span className={styles.legendItem}>
                        <span className={styles.legendDot} style={{ background: "#16a34a" }} />
                        Posted
                    </span>
                    <span className={styles.legendItem}>
                        <span className={styles.legendDot} style={{ background: "#dc2626" }} />
                        Failed
                    </span>
                </div>
            </div>

            {/* Calendar */}
            <div className={styles.calendarWrapper}>
                {initialLoading ? (
                    <div className={styles.loading}>Loading calendar...</div>
                ) : (
                    <FullCalendar
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                        initialView="dayGridMonth"
                        headerToolbar={{
                            left: "prev,next today",
                            center: "title",
                            right: "dayGridMonth,timeGridWeek",
                        }}
                        events={events}
                        eventClick={handleEventClick}
                        datesSet={handleDatesSet}
                        height="auto"
                        eventDisplay="block"
                        dayMaxEvents={3}
                        moreLinkClick="popover"
                    />
                )}
            </div>

            {/* Post Detail Modal */}
            {showDetailModal && selectedPost && (
                <div className={styles.modalOverlay} onClick={() => setShowDetailModal(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>Post Details</h2>
                            <button
                                className={styles.closeButton}
                                onClick={() => setShowDetailModal(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <div className={styles.postDetail}>
                                <span className={styles.detailLabel}>Specialty</span>
                                <span className={styles.detailValue}>{selectedPost.specialty}</span>
                            </div>
                            <div className={styles.postDetail}>
                                <span className={styles.detailLabel}>Question</span>
                                <span className={styles.detailValue}>{selectedPost.question_preview}</span>
                            </div>
                            <div className={styles.postDetail}>
                                <span className={styles.detailLabel}>Scheduled For</span>
                                <span className={styles.detailValue}>{formatTime(selectedPost.scheduled_for)}</span>
                            </div>
                            <div className={styles.postDetail}>
                                <span className={styles.detailLabel}>Status</span>
                                <span
                                    className={styles.statusBadge}
                                    style={{
                                        backgroundColor: STATUS_COLORS[selectedPost.scheduling_status]?.bg || "#6b7280",
                                    }}
                                >
                                    {selectedPost.scheduling_status}
                                </span>
                            </div>
                        </div>

                        <div className={styles.modalFooter}>
                            {selectedPost.scheduling_status === "scheduled" && (
                                <>
                                    <button
                                        className={styles.secondaryButton}
                                        onClick={() => handleUnschedule(selectedPost.id)}
                                        disabled={actionLoading}
                                    >
                                        {actionLoading ? "..." : "Unschedule"}
                                    </button>
                                    <button
                                        className={styles.secondaryButton}
                                        onClick={() => handleReschedule(selectedPost.id, selectedPost.scheduled_for)}
                                        disabled={actionLoading}
                                    >
                                        Reschedule
                                    </button>
                                    <button
                                        className={styles.primaryButton}
                                        onClick={() => handleMarkPosted(selectedPost.id)}
                                        disabled={actionLoading}
                                    >
                                        {actionLoading ? "..." : "Mark as Posted"}
                                    </button>
                                </>
                            )}
                            {selectedPost.scheduling_status === "failed" && (
                                <>
                                    <button
                                        className={styles.secondaryButton}
                                        onClick={() => handleReschedule(selectedPost.id, selectedPost.scheduled_for)}
                                        disabled={actionLoading}
                                    >
                                        Reschedule
                                    </button>
                                    <button
                                        className={styles.primaryButton}
                                        onClick={() => handleRetry(selectedPost.id)}
                                        disabled={actionLoading}
                                    >
                                        {actionLoading ? "Retrying..." : "Retry Post"}
                                    </button>
                                </>
                            )}
                            <button
                                className={styles.secondaryButton}
                                onClick={() => {
                                    setShowDetailModal(false);
                                    navigate(`/admin/instagram?bank=${bank}`);
                                }}
                            >
                                View Full Details
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Schedule Modal */}
            {showScheduleModal && reschedulingPostId && (
                <ScheduleModal
                    onSchedule={handleScheduleSubmit}
                    onClose={() => {
                        setShowScheduleModal(false);
                        setReschedulingPostId(null);
                        setReschedulingInitialDate(null);
                    }}
                    initialDate={reschedulingInitialDate}
                />
            )}
        </div>
    );
};

export default AdminInstagramCalendar;
